import { useReportStateContext } from "./useReportState";
import { RANGE_VALUE_TYPE_NUMBER } from "components/reportsV3/types/constants";
import { filterValueMultiplier } from "components/reportsV3/utils/numberUtils";
import {
  buildGroupingColumnName,
  defaultNoValueEntry,
  getActiveRangeGroupings,
  parseNoValueEntry,
  parseRangeRows,
  rangeValueType,
} from "components/reportsV3/utils/rangeGroupingUtils";
import {
  addToSet,
  insertBefore,
  removeFromArray,
  removeFromSet,
  withoutKey,
} from "components/reportsV3/utils/stateUpdaterUtils";
import { useCallback, useMemo, useState } from "react";

const CLOSED_DIALOG = {
  open: false,
  sourceColumn: null,
  sourceDisplayName: "",
  initialRanges: [],
  initialNoValue: defaultNoValueEntry(),
  valueType: RANGE_VALUE_TYPE_NUMBER,
  subType: null,
  isEdit: false,
};

/** Manages range groupings and the range grouping dialog for the current tab. */
export function useRangeGrouping({
  availableColumns,
  getColumnDisplayName,
  getColumnType,
  getColumnSubType,
  onUngroupColumn,
}) {
  const {
    rangeGroupings,
    setRangeGroupings,
    groupByColumns,
    setGroupByColumns,
    setAllValuesEnumerations,
    setColumnOrder,
    setColumnNameOverrides,
    setPinnedColumns,
    setColumnWidths,
    setSummarySelections,
    setColumnVisibilityModel,
    clearFilter,
  } = useReportStateContext();

  const [dialog, setDialog] = useState(CLOSED_DIALOG);

  const activeRangeGroupings = useMemo(
    () =>
      getActiveRangeGroupings(rangeGroupings, groupByColumns, availableColumns),
    [rangeGroupings, groupByColumns, availableColumns]
  );

  const isRangeGrouped = useCallback(
    (column) =>
      Object.prototype.hasOwnProperty.call(activeRangeGroupings, column),
    [activeRangeGroupings]
  );

  const openRangeGrouping = useCallback(
    (column) =>
      setDialog({
        open: true,
        sourceColumn: column,
        sourceDisplayName: getColumnDisplayName(column),
        initialRanges: [],
        initialNoValue: defaultNoValueEntry(),
        valueType: rangeValueType(getColumnType?.(column)),
        subType: getColumnSubType?.(column) ?? null,
        isEdit: false,
      }),
    [getColumnDisplayName, getColumnType, getColumnSubType]
  );

  const editRangeGrouping = useCallback(
    (column) => {
      const expression = rangeGroupings?.[column]?.expression;
      const valueType = rangeValueType(getColumnType?.(column));
      const subType = getColumnSubType?.(column) ?? null;
      setDialog({
        open: true,
        sourceColumn: column,
        sourceDisplayName: getColumnDisplayName(column),
        initialRanges: parseRangeRows(
          expression,
          valueType === RANGE_VALUE_TYPE_NUMBER
            ? filterValueMultiplier(subType)
            : 1
        ),
        initialNoValue: parseNoValueEntry(expression, valueType),
        valueType,
        subType,
        isEdit: true,
      });
    },
    [getColumnDisplayName, getColumnType, getColumnSubType, rangeGroupings]
  );

  const onClose = useCallback(() => setDialog(CLOSED_DIALOG), []);

  const onSave = useCallback(
    (entry) => {
      const source = dialog.sourceColumn;
      const existing = rangeGroupings?.[source];
      if (dialog.isEdit && existing) {
        setRangeGroupings((prev) => ({
          ...prev,
          [source]: { ...prev[source], expression: entry.expression },
        }));
        clearFilter(buildGroupingColumnName(source));
      } else {
        setRangeGroupings((prev) => ({ ...prev, [source]: entry }));
        setGroupByColumns(addToSet(entry.column));
        setAllValuesEnumerations(addToSet(entry.column));
        setColumnOrder(insertBefore(entry.column, source));
      }
      clearFilter(source);
      setDialog(CLOSED_DIALOG);
    },
    [
      dialog,
      rangeGroupings,
      setRangeGroupings,
      setGroupByColumns,
      setAllValuesEnumerations,
      setColumnOrder,
      clearFilter,
    ]
  );

  const ungroupRangeGrouping = useCallback(
    (column) => {
      const grouping = buildGroupingColumnName(column);
      setAllValuesEnumerations(removeFromSet(grouping));
      setColumnOrder(removeFromArray(grouping));
      setRangeGroupings(withoutKey(column));
      setColumnNameOverrides(withoutKey(grouping));
      setColumnWidths(withoutKey(grouping));
      setSummarySelections(withoutKey(grouping));
      setColumnVisibilityModel(withoutKey(grouping));
      setPinnedColumns(removeFromArray(grouping));
      clearFilter(grouping);
      onUngroupColumn(grouping);
    },
    [
      setAllValuesEnumerations,
      setColumnOrder,
      setRangeGroupings,
      setColumnNameOverrides,
      setColumnWidths,
      setSummarySelections,
      setColumnVisibilityModel,
      setPinnedColumns,
      clearFilter,
      onUngroupColumn,
    ]
  );

  const dialogProps = useMemo(
    () => ({
      open: dialog.open,
      sourceColumn: dialog.sourceColumn,
      sourceDisplayName: dialog.sourceDisplayName,
      initialRanges: dialog.initialRanges,
      initialNoValue: dialog.initialNoValue,
      valueType: dialog.valueType,
      subType: dialog.subType,
      onSave,
      onClose,
    }),
    [dialog, onSave, onClose]
  );

  return {
    activeRangeGroupings,
    isRangeGrouped,
    dialogProps,
    openRangeGrouping,
    editRangeGrouping,
    ungroupRangeGrouping,
  };
}
